/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    Actions,
    concatLatestFrom,
    createEffect,
    ofType,
} from '@ngrx/effects';
import { of } from 'rxjs';
import {
    catchError,
    delay,
    map,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { DevLoggerService } from 'ng/modules/core/services/dev-logger.service';

import { SystemInfoService } from './system-info.service';
import * as SystemInfoActions from './system-info.actions';
import { selectAllowReload } from './system-info.selectors';
import { SYSTEM_INFO_FRESHNESS_MAX_AGE } from './system-info.constants';

@Injectable()
export class SystemInfoEffects {
    /**
     * Force load system info data, regarless of data freshness max age.
     */
    public loadSystemInfo$ = createEffect(() => this.actions$.pipe(
        ofType(SystemInfoActions.loadSystemInfo),
        switchMap(() => {
            return this.systemInfoService.loadSystemInfo().pipe(
                // on-going loading is aborted if the cancelLoading action has been dispatched
                takeUntil(this.actions$.pipe(
                    ofType(SystemInfoActions.cancelLoading),
                )),
                map(
                    systemInfoApiResponse => SystemInfoActions.systemInfoLoadSuccess(
                        { systemInfo: systemInfoApiResponse },
                    ),
                ),
                catchError(error => {
                    return of(SystemInfoActions.systemInfoLoadFailure({ error }));
                }),
            );
        }),
    ));

    /**
     * Load system info data, complying with a cache freshness period, within which reload is not
     * allowed.
     */
    public passiveLoadSystemInfo$ = createEffect(() => this.actions$.pipe(
        ofType(SystemInfoActions.passiveLoadSystemInfo),
        concatLatestFrom(() => this.store.select(selectAllowReload)),
        switchMap(([loadAction, allowReload]) => {
            if (allowReload) {
                return of(SystemInfoActions.loadSystemInfo());
            }

            // when data hasn't expired (allowReload is false), prevent loading
            return of(SystemInfoActions.cancelLoading());
        }),
    ));

    /**
     * Allow reload 15s after the last successful load call.
     */
    public systemInfoLoadSuccess$ = createEffect(
        () => this.actions$.pipe(
            ofType(SystemInfoActions.systemInfoLoadSuccess),
            switchMap(
                () => of(SystemInfoActions.setAllowReload({ allowReload: true }))
                    .pipe(delay(SYSTEM_INFO_FRESHNESS_MAX_AGE * 1000)),
            ),
        ),
    );

    /**
     * Log the error upon load call failure.
     */
    public systemInfoLoadFailure$ = createEffect(
        () => this.actions$.pipe(
            ofType(SystemInfoActions.systemInfoLoadFailure),
            tap(({ error }) => this.devLoggerService.error(error.data)),
        ),
        { dispatch: false },
    );

    constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly systemInfoService: SystemInfoService,
        private readonly devLoggerService: DevLoggerService,
    ) {}
}
